'use client';

import { FC, useEffect, useState } from 'react';

import { type FeatureToggle } from '@ravago/shared/page-data/models/config';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { ShoppingCartItem } from '@ravago/shared/page-data/models/features';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { LocalDateTime } from '@js-joda/core';

import { useRouter } from 'next/navigation';

import { useShoppingCartContext } from '../../../../context/shopping-cart-context';
import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import CheckoutSuccessIllustration from '../../../illustrations/checkout-success-illustration';
import OrderNotFoundIllustration from '../../../illustrations/order-not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';

import { CheckoutStep } from './checkout-overview/checkout-step';
import { CheckoutSummary } from './checkout-overview/checkout-summary';
import { CheckoutSuccessOrderInfo } from './checkout-success/checkout-success-order-info';
import { CheckoutSuccessSummary } from './checkout-success/checkout-success-summary';

interface Props {
   features: FeatureToggle[];
   shoppingCartItems: Array<ShoppingCartItem>;
   locale: string;
   deliveryAddress: Address | undefined;
   legalAddress: Address | undefined;
   hasProblems: boolean;
}

export const Checkout: FC<Props> = ({ features, shoppingCartItems, locale, deliveryAddress, legalAddress, hasProblems }) => {
   const translate = useTranslations();
   const router = useRouter();
   type CheckoutStatus = 'success' | 'fail' | 'overview' | 'loading';
   const languageCode = getLanguageCode(locale);

   const [deliveryDate, setDeliveryDate] = useState<Date>(new Date(DateUtils.getNextBusinessDay(LocalDateTime.now(), 2, 16)));

   const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatus>('overview');

   const { resetCartValidation } = useShoppingCartContext();

   const handleChangeCheckoutStatus = (status: CheckoutStatus) => {
      if (status === 'success') {
         resetCartValidation();
      }
      setCheckoutStatus(status);
   };

   useEffect(() => {
      if (shoppingCartItems.length === 0 || !legalAddress || !deliveryAddress) {
         router.push(`/${languageCode}/my-shopping-cart`);
      }
   }, [shoppingCartItems, legalAddress, deliveryAddress, languageCode, router]);

   if (checkoutStatus === 'loading') {
      return (
         <Box direction="column" items="center">
            <Spinner />
         </Box>
      );
   }

   if (shoppingCartItems.length !== 0 && legalAddress && deliveryAddress) {
      const checkoutOverview = (
         <Box direction="column">
            {hasProblems && (
               <Alert variant="error" closable={false} headline="Error">
                  {translate('shopping-cart.checkout.remaining-errors')}
               </Alert>
            )}

            <Grid>
               <GridCol spanDesktop={8} spanTablet={8} spanPhone={4}>
                  <CheckoutStep
                     features={features}
                     handleSetDeliveryDate={setDeliveryDate}
                     changeCheckoutStatus={handleChangeCheckoutStatus}
                     hasProblems={hasProblems}
                     locale={locale}
                     legalAddress={legalAddress}
                     deliveryAddress={deliveryAddress}
                  />
               </GridCol>

               <GridCol spanDesktop={4} spanTablet={8} spanPhone={4} startDesktop={9} startTablet={1} startPhone={1}>
                  <CheckoutSummary locale={locale} shoppingCartItems={shoppingCartItems} />
               </GridCol>
            </Grid>
         </Box>
      );

      const checkoutSuccess = (
         <Box direction="column" gap="3xl">
            <Box direction="column" items="center">
               <Box direction="column" items="center" gap="xl">
                  <Box direction="column" items="center" gap="xl">
                     <CheckoutSuccessIllustration />
                     <Typography component="h4" variant="heading-4">
                        {translate('shopping-cart.checkout-summary-header')}
                     </Typography>
                  </Box>
                  <Box maxWidth="600px" direction="column">
                     <Box items="start">
                        <Icon icon="envelope-open-text" />
                        <Typography>{translate('shopping-cart.checkout-summary-instruction-1')}</Typography>
                     </Box>

                     <Box items="start">
                        <Icon icon="dolly" />
                        <Typography>{translate('shopping-cart.checkout-summary-instruction-2')}</Typography>
                     </Box>

                     <Box items="start">
                        <Icon icon="truck" />
                        <Typography>{translate('shopping-cart.checkout-summary-instruction-3')}</Typography>
                     </Box>
                  </Box>
               </Box>

               <BreakpointSwitch excludeViews={['tab-port']}>
                  <Box justify="between">
                     <Grid>
                        <GridCol spanDesktop={6} startDesktop={1} spanTablet={8} startTablet={1} spanPhone={4} startPhone={1}>
                           <CheckoutSuccessOrderInfo
                              deliveryDate={deliveryDate}
                              deliveryAddress={deliveryAddress}
                              legalAddress={legalAddress}
                              locale={locale}
                           />
                        </GridCol>
                        <GridCol spanDesktop={6} startDesktop={7} spanTablet={8} startTablet={1} spanPhone={4} startPhone={1}>
                           <CheckoutSuccessSummary shoppingCartItems={shoppingCartItems} />
                        </GridCol>
                     </Grid>
                  </Box>
               </BreakpointSwitch>

               <BreakpointSwitch includeViews={['tab-port']}>
                  <Box direction="column">
                     <CheckoutSuccessOrderInfo
                        deliveryDate={deliveryDate}
                        deliveryAddress={deliveryAddress}
                        legalAddress={legalAddress}
                        locale={locale}
                     />
                     <CheckoutSuccessSummary shoppingCartItems={shoppingCartItems} />
                  </Box>
               </BreakpointSwitch>
            </Box>
         </Box>
      );

      const checkoutFail = (
         <Grid>
            <GridCol spanDesktop={6} spanTablet={6} spanPhone={4} startDesktop={4} startTablet={2} startPhone={1}>
               <Box direction="column" items="center">
                  <EmptyState
                     image={<OrderNotFoundIllustration />}
                     title={translate('shopping-cart.checkout.fail-title')}
                     content={translate('shopping-cart.checkout.fail-description')}
                  />
               </Box>
            </GridCol>
         </Grid>
      );

      return (
         <Box direction="column">
            {checkoutStatus === 'overview' && checkoutOverview}
            {checkoutStatus === 'success' && checkoutSuccess}
            {checkoutStatus === 'fail' && checkoutFail}
         </Box>
      );
   }
   return null;
};
